/**
 * Three-tier logout utilities.
 *
 * SUPREME RULE: User data must NEVER be silently deleted.
 * See policies/never-do.md NEVER-01, NEVER-17.
 *
 * Tier 1 — Soft re-auth: Token expired → show login UI, preserve everything.
 *          (Handled automatically by AuthGate when DXCUserInteraction fires)
 *
 * Tier 2 — Standard sign-out: User clicks "Sign out" →
 *          Try sync → db.cloud.logout() → clear user caches → reload.
 *
 * Tier 3 — Hard logout: User clicks "Delete all data" or remote force_logout →
 *          db.cloud.logout() → Dexie.delete() → localStorage.clear() → reload.
 */

import Dexie from "dexie"
import { db } from "@/db/schema"
import { DEXIE_DB_NAME } from "@/config/environment"
import { clearDeviceIdCache } from "@/shared/utils/device/stableDeviceId"

// ---------------------------------------------------------------------------
// localStorage cleanup
// ---------------------------------------------------------------------------

/**
 * Keys that are device-specific and should survive a sign-out.
 *
 * Everything NOT in this list is treated as user-specific and will be removed
 * when the user signs out (Tier 2). This whitelist approach is intentionally
 * conservative: any new localStorage key added in the future will be
 * automatically cleared on logout unless explicitly preserved here.
 *
 * ⚠️  SECURITY: If you add a new localStorage key that contains user data,
 *     do NOT add it to this whitelist.
 */
const DEVICE_ONLY_KEYS: ReadonlySet<string> = new Set([
  // Legacy persistent device identifier (may exist on upgraded installs)
  "totodo_stable_device_id",
  // Legacy migration marker
  "totodo_stable_device_id:claimed",
  // Language preference — device-level, not account-level
  "totodo:language",
])

/**
 * Prefixes of keys that are device-specific and should survive sign-out.
 * Per-user device IDs (totodo_device_id:<email>) are UUIDs — not sensitive.
 */
const DEVICE_ONLY_PREFIXES: readonly string[] = ["totodo_device_id:"]

/**
 * Removes all user-specific data from localStorage while preserving
 * device-only keys (see DEVICE_ONLY_KEYS whitelist).
 *
 * Called by both standardSignOut() and forceSignOut() to prevent cached
 * data from one account bleeding into another login session.
 * Also called by DatabaseProvider when the login screen is shown —
 * ensures no placeholder cache leaks data if auth is gone.
 */
export function clearUserLocalStorage(): void {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key) continue
    if (DEVICE_ONLY_KEYS.has(key)) continue
    if (DEVICE_ONLY_PREFIXES.some((p) => key.startsWith(p))) continue
    keysToRemove.push(key)
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key)
  }
}

// ---------------------------------------------------------------------------
// Tier 2: Standard Sign-Out
// ---------------------------------------------------------------------------

/**
 * Signs the user out while preserving IndexedDB data (for re-login).
 *
 * 1. Attempts to push unsynced changes first
 * 2. On failure, returns false so the caller can warn the user
 * 3. Calls db.cloud.logout() — clears auth tokens, keeps IndexedDB
 * 4. Clears ALL user-specific localStorage caches (security: prevents
 *    data from leaking to a different account on the same device)
 * 5. Full page reload to reset all in-memory state
 *
 * @returns `true` if sign-out completed, `false` if there are unsynced changes
 *          and the caller should show a confirmation dialog first.
 */
export async function standardSignOut(): Promise<boolean> {
  // 1. Try to sync pending changes
  try {
    await db.cloud.sync({ purpose: "push", wait: true })
  } catch {
    // Sync failed — caller should warn user about unsynced data
    return false
  }

  // 2. Clear auth — data stays in IndexedDB
  await db.cloud.logout()

  // 3. Clear in-memory device ID cache (next login may be a different user)
  clearDeviceIdCache()

  // 4. Clear all user-specific localStorage (whitelist approach)
  clearUserLocalStorage()

  // 5. Full reload to reset in-memory state
  window.location.href = "/"

  return true
}

/**
 * Force sign-out — skips sync attempt, used when user confirms they
 * want to sign out despite unsynced changes.
 */
export async function forceSignOut(): Promise<void> {
  await db.cloud.logout()
  clearDeviceIdCache()
  clearUserLocalStorage()
  window.location.href = "/"
}

// ---------------------------------------------------------------------------
// Tier 3: Hard Logout (complete data wipe)
// ---------------------------------------------------------------------------

/**
 * Completely wipes all local data and signs out.
 *
 * ONLY call this when:
 * - User explicitly clicks "Delete all data" in settings
 * - Remote force_logout received from device management
 *
 * NEVER call this from error recovery, auth flows, or automatic processes.
 * See NEVER-01.
 */
export async function hardLogout(): Promise<void> {
  // 1. Disconnect from cloud first
  try {
    await db.cloud.logout()
  } catch {
    // Continue even if logout fails — we're wiping everything anyway
  }

  // 2. Delete the entire IndexedDB database
  try {
    await Dexie.delete(DEXIE_DB_NAME)
  } catch {
    // Best effort — some browsers may block this
  }

  // 3. Clear ALL localStorage (this is the one place it's allowed)
  localStorage.clear()

  // 4. Full reload — fresh start
  window.location.href = "/"
}
